import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { createApiClient, apiClient } from "../../api/apiClient";
import { handleNHOErrorWithFriendlyMessage } from "../utils/nhoErrorHandler";

// Define the types
export interface AdminTarget {
  Id?: number;
  DPS_Target: number;
  CPH_Target: number;
  GAP_Time_Target: number; // We keep this casing internally for consistency
  SIA_Target: number;
  Attendance: number;
  Week: string; // Format is "Week1", "Week2", etc.
}

// Interface for API request/response to handle the Gap_Time_Target field name difference
export interface AdminTargetApiFormat {
  Id?: number;
  DPS_Target: number;
  CPH_Target: number;
  Gap_Time_Target: number; // API format has lowercase 'a'
  SIA_Target: number;
  Attendance: number;
  Week: string;
}

// Define the payload for saving targets
export interface SaveAdminTargetsPayload {
  Location_Name: string;
  updates: AdminTarget[];
}

// Internal function to convert AdminTarget to AdminTargetApiFormat
function convertToApiFormat(target: AdminTarget): AdminTargetApiFormat {
  return {
    Id: target.Id || 0, // Ensure ID is always provided, default to 0 if undefined
    Week: target.Week,
    DPS_Target: target.DPS_Target,
    CPH_Target: target.CPH_Target,
    Gap_Time_Target: target.GAP_Time_Target || 0, // Note the casing difference, ensure it's not null/undefined
    SIA_Target: target.SIA_Target,
    Attendance: target.Attendance,
  };
}

// Internal function to convert AdminTargetApiFormat to AdminTarget
function convertFromApiFormat(apiTarget: AdminTargetApiFormat): AdminTarget {
  return {
    Id: apiTarget.Id,
    Week: apiTarget.Week,
    DPS_Target: apiTarget.DPS_Target,
    CPH_Target: apiTarget.CPH_Target,
    GAP_Time_Target: apiTarget.Gap_Time_Target || 0, // Note the casing difference, ensure it's not null/undefined
    SIA_Target: apiTarget.SIA_Target,
    Attendance: apiTarget.Attendance,
  };
}

export interface NHOAdminState {
  targets: AdminTarget[];
  loading: boolean;
  error: string | null;
  retryCount: number;
  saveLoading: boolean;
  saveError: string | null;
  saveSuccess: boolean;
  deleteLoading: boolean;
  deleteError: string | null;
  deleteSuccess: boolean;
}

// Initial state
const initialState: NHOAdminState = {
  targets: [],
  loading: false,
  error: null,
  retryCount: 0,
  saveLoading: false,
  saveError: null,
  saveSuccess: false,
  deleteLoading: false,
  deleteError: null,
  deleteSuccess: false,
};

// Async thunk for fetching admin targets
export const fetchAdminTargets = createAsyncThunk(
  "nhoAdmin/fetchAdminTargets",
  async (location: string, { rejectWithValue }) => {
    try {
      // Create API client
      const api = createApiClient();

      // Build URL with location parameter
      const url = `/nho/admin/targets?Location_Name=${encodeURIComponent(
        location
      )}`;

      // Make API call
      const response = await apiClient(api, url, "GET");

      // Log the raw response to debug GAP time issues
      console.log("fetchAdminTargets raw response:", response);

      // Convert from API format to internal format
      const convertedResponse = Array.isArray(response)
        ? response.map((target: AdminTargetApiFormat) => {
            const converted = convertFromApiFormat(target);
            console.log("Converted target:", converted);
            return converted;
          })
        : [];

      // Return the converted data
      return convertedResponse;
    } catch (error: any) {
      // Surface centralized NHO modal with friendly message and context
      try {
        handleNHOErrorWithFriendlyMessage('FETCH_ADMIN_TARGETS', error, true, { location });
      } catch (e) {
        // swallow any secondary errors from the handler to avoid masking the original rejection
        console.error('Error while handling NHO error for fetchAdminTargets', e);
      }
      return rejectWithValue(error.message || "Failed to fetch admin targets");
    }
  }
);

// Async thunk for saving admin targets
export const saveAdminTargets = createAsyncThunk(
  "nhoAdmin/saveAdminTargets",
  async (payload: SaveAdminTargetsPayload, { rejectWithValue }) => {
    try {
      // Create API client
      const api = createApiClient();

      // Ensure all records have valid values for all required fields
      const validatedUpdates = payload.updates.map((record) => ({
        ...record,
        Id: record.Id || 0,
        DPS_Target: record.DPS_Target || 0,
        CPH_Target: record.CPH_Target || 0,
        GAP_Time_Target: record.GAP_Time_Target || 0,
        SIA_Target: record.SIA_Target || 0,
        Attendance: record.Attendance || 0,
      }));

      // Convert to API format
      const apiPayload = {
        Location_Name: payload.Location_Name,
        updates: validatedUpdates.map(convertToApiFormat),
      };

      // Log the payload being sent for debugging
      console.log(
        "Saving admin targets - total records:",
        apiPayload.updates.length
      );
      console.log("Record details:");
      apiPayload.updates.forEach((update, index) => {
        console.log(
          `Record ${index + 1}: Week=${update.Week}, GAP_Time_Target=${
            update.Gap_Time_Target
          }`
        );
      });

      // Make API call to save updates
      const response = await apiClient(
        api,
        "/nho/admin/targets",
        "PUT",
        apiPayload
      );

      // Log the response for debugging
      console.log(
        "Save admin targets response received:",
        response ? "Yes" : "No"
      );

      // Return both the response data and the original updates for reference
      return {
        response,
        updatedRecords: validatedUpdates,
      };
    } catch (error: any) {
      console.error("Error saving admin targets:", error);
      try {
        handleNHOErrorWithFriendlyMessage('SAVE_ADMIN_TARGETS', error, true, { payload });
      } catch (e) {
        console.error('Error while handling NHO error for saveAdminTargets', e);
      }
      return rejectWithValue(error.message || "Failed to save admin targets");
    }
  }
);

// Interface for delete target payload
export interface DeleteAdminTargetPayload {
  id: number; // We'll still track the id for UI state management, but won't send it in the API call
  locationName: string; // Location_Name to be sent as URL parameter
}

// Async thunk for deleting admin target
export const deleteAdminTarget = createAsyncThunk(
  "nhoAdmin/deleteAdminTarget",
  async (payload: DeleteAdminTargetPayload, { rejectWithValue }) => {
    try {
      // Create API client
      const api = createApiClient();

      // Construct URL with Location_Name as a URL parameter
      const url = `/nho/admin/targets?Location_Name=${encodeURIComponent(
        payload.locationName
      )}`;

      console.log("nhoAdminSlice: Making DELETE API call to URL:", url);

      // Make API call to delete target
      const response = await apiClient(api, url, "DELETE");

      // Return the response data along with the deleted id for reference (for local state management)
      return { response, deletedId: payload.id };
    } catch (error: any) {
      console.error("nhoAdminSlice: DELETE API call failed:", error);
      try {
        handleNHOErrorWithFriendlyMessage('DELETE_ADMIN_TARGET', error, true, { payload });
      } catch (e) {
        console.error('Error while handling NHO error for deleteAdminTarget', e);
      }
      return rejectWithValue(error.message || "Failed to delete target");
    }
  }
);

// Interface for add target payload
export interface AddAdminTargetPayload {
  Location_Name: string;
  newTarget: AdminTarget;
}

// Async thunk for adding admin target
export const addAdminTarget = createAsyncThunk(
  "nhoAdmin/addAdminTarget",
  async (payload: AddAdminTargetPayload, { rejectWithValue }) => {
    try {
      // Create API client
      const api = createApiClient();

      // Prepare the request payload in the format expected by the API
      const requestPayload = {
        Location_Name: payload.Location_Name,
        updates: [convertToApiFormat(payload.newTarget)],
      };

      // Make API call to add target (uses PUT endpoint with the single target)
      const response = await apiClient(
        api,
        "/nho/admin/targets",
        "PUT",
        requestPayload
      );

      // Return the response data along with the added target for reference
      return { response, addedTarget: payload.newTarget };
    } catch (error: any) {
      try {
        handleNHOErrorWithFriendlyMessage('ADD_ADMIN_TARGET', error, true, { payload });
      } catch (e) {
        console.error('Error while handling NHO error for addAdminTarget', e);
      }
      return rejectWithValue(error.message || "Failed to add new target");
    }
  }
);

// Interface for add target with POST method (follows exact schema provided)
export interface AddAdminTargetPostPayload {
  Location_Name: string;
  DPS_Target: number;
  CPH_Target: number;
  GAP_Time_Target: number;
  SIA_Target: number;
  Attendance: number;
}

// Async thunk for adding admin target using POST method
export const addAdminTargetPost = createAsyncThunk(
  "nhoAdmin/addAdminTargetPost",
  async (payload: AddAdminTargetPostPayload, { rejectWithValue }) => {
    try {
      // Create API client
      const api = createApiClient();

      // Convert the payload to API format (GAP_Time_Target -> Gap_Time_Target)
      const apiPayload = {
        Location_Name: payload.Location_Name,
        DPS_Target: payload.DPS_Target,
        CPH_Target: payload.CPH_Target,
        Gap_Time_Target: payload.GAP_Time_Target, // Convert to API format with lowercase 'a'
        SIA_Target: payload.SIA_Target,
        Attendance: payload.Attendance,
      };

      // Make API call to add target using POST endpoint with API format
      const response = await apiClient(
        api,
        "/nho/admin/targets",
        "POST",
        apiPayload
      );

      // Return the response data along with the added target for reference
      // Log the response to debug
      console.log("Add target POST response:", response);

      return {
        response,
        addedTarget: {
          Week: `Week${response.Week || (response.Id ? response.Id : "")}`, // Try to derive Week from response
          DPS_Target: payload.DPS_Target,
          CPH_Target: payload.CPH_Target,
          GAP_Time_Target: payload.GAP_Time_Target || 0,
          SIA_Target: payload.SIA_Target,
          Attendance: payload.Attendance,
          Id: response.Id, // Get the Id from response if available
        },
      };
    } catch (error: any) {
      try {
        handleNHOErrorWithFriendlyMessage('ADD_ADMIN_TARGET_POST', error, true, { payload });
      } catch (e) {
        console.error('Error while handling NHO error for addAdminTargetPost', e);
      }
      return rejectWithValue(error.message || "Failed to add new target");
    }
  }
);

// Create the slice
const nhoAdminSlice = createSlice({
  name: "nhoAdmin",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSaveError: (state) => {
      state.saveError = null;
    },
    clearSaveSuccess: (state) => {
      state.saveSuccess = false;
    },
    clearDeleteError: (state) => {
      state.deleteError = null;
    },
    clearDeleteSuccess: (state) => {
      state.deleteSuccess = false;
    },
    incrementRetryCount: (state) => {
      state.retryCount += 1;
    },
    resetRetryCount: (state) => {
      state.retryCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handling fetchAdminTargets
      .addCase(fetchAdminTargets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAdminTargets.fulfilled,
        (state, action: PayloadAction<AdminTarget[]>) => {
          state.loading = false;
          state.targets = action.payload;
          state.error = null;
          state.retryCount = 0; // Reset retry count on successful fetch
        }
      )
      .addCase(fetchAdminTargets.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch admin targets";
      })

      // Handling saveAdminTargets
      .addCase(saveAdminTargets.pending, (state) => {
        state.saveLoading = true;
        state.saveError = null;
        state.saveSuccess = false;
      })
      .addCase(saveAdminTargets.fulfilled, (state, action) => {
        state.saveLoading = false;
        state.saveSuccess = true;
        state.saveError = null;

        // We will NOT update the state.targets directly here
        // Instead, we'll let the fetchAdminTargets call handle the refresh
        // This prevents duplicate refreshes and ensures consistent state
        if (action.payload && action.payload.updatedRecords) {
          console.log(
            "Save successful with records:",
            action.payload.updatedRecords.length
          );
          // The updated data will be fetched by the API refresh in AdminPage
        }
      })
      .addCase(saveAdminTargets.rejected, (state, action) => {
        state.saveLoading = false;
        state.saveError =
          (action.payload as string) || "Failed to save admin targets";
        state.saveSuccess = false;
      })

      // Handling deleteAdminTarget
      .addCase(deleteAdminTarget.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteAdminTarget.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deleteSuccess = true;
        state.deleteError = null;

        // Only remove the deleted item from state if there are more than 4 rows
        if (
          action.payload &&
          action.payload.deletedId &&
          state.targets.length > 4
        ) {
          state.targets = state.targets.filter(
            (target) => target.Id !== action.payload.deletedId
          );

          // If we somehow end up with fewer than 4 rows, the UI will prevent further deletions
          // but we'll check here just to be safe
          if (state.targets.length < 4) {
            state.deleteError = "Cannot delete: minimum 4 rows required";
            state.deleteSuccess = false;
          }
        }
      })
      .addCase(deleteAdminTarget.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError =
          (action.payload as string) || "Failed to delete target";
        state.deleteSuccess = false;
      })

      // Handling addAdminTarget (legacy PUT method)
      .addCase(addAdminTarget.pending, (state) => {
        state.saveLoading = true;
        state.saveError = null;
        state.saveSuccess = false;
      })
      .addCase(addAdminTarget.fulfilled, (state, action) => {
        state.saveLoading = false;
        state.saveSuccess = true;
        state.saveError = null;

        // Add the new target to the state
        state.targets.push(action.payload.addedTarget);
      })
      .addCase(addAdminTarget.rejected, (state, action) => {
        state.saveLoading = false;
        state.saveError =
          (action.payload as string) || "Failed to add new target";
        state.saveSuccess = false;
      })

      // Handling addAdminTargetPost - New POST method for adding targets
      .addCase(addAdminTargetPost.pending, (state) => {
        state.saveLoading = true;
        state.saveError = null;
        state.saveSuccess = false;
      })
      .addCase(addAdminTargetPost.fulfilled, (state, action) => {
        state.saveLoading = false;
        state.saveSuccess = true;
        state.saveError = null;

        // Calculate the correct week number based on current targets
        const weekNumber = state.targets.length + 1;
        const weekName = `Week${weekNumber}`;

        // Add the new target to the state with proper Week name and ID from response
        const newTarget = {
          ...action.payload.addedTarget,
          Week: weekName, // Use the calculated week name
          Id: action.payload.response.Id || state.targets.length + 1, // Ensure we have a valid ID
          // Explicitly ensure GAP_Time_Target is set correctly
          GAP_Time_Target: action.payload.addedTarget.GAP_Time_Target || 100,
        };

        console.log("Adding new target to Redux state:", newTarget);
        state.targets.push(newTarget);
      })
      .addCase(addAdminTargetPost.rejected, (state, action) => {
        state.saveLoading = false;
        state.saveError =
          (action.payload as string) || "Failed to add new target";
        state.saveSuccess = false;
      });
  },
});

// Export actions and reducer
export const {
  clearError,
  clearSaveError,
  clearSaveSuccess,
  clearDeleteError,
  clearDeleteSuccess,
  incrementRetryCount,
  resetRetryCount,
} = nhoAdminSlice.actions;

export default nhoAdminSlice.reducer;
